import { Component, computed, inject, input, Input, signal } from '@angular/core'
import { FilterService } from 'src/app/services/filter.service'
import { mapNumberToLocaleString } from "../../helpers";
import {
  FilterSelectionsInterface,
  PictureInterface,
  Product,
  ProductAttributeInterface,
  ProductVariantInterface
} from "@gea-waldviertler-website/gea-models";
import { PageService } from "@gea-waldviertler-website/shop-lib";

@Component({
    selector: 'gea-shop-product-item',
    templateUrl: './item.component.html',
    styleUrls: ['./item.component.scss'],
    standalone: false
})
export class ItemComponent {

  product = input.required<Product<ProductVariantInterface>>()
  
  @Input() productAttributes?: Map<string, ProductAttributeInterface>
  presetVariant = input<ProductVariantInterface | undefined>()
  @Input({ required: true }) scope!: "search" | "list"

  // Effektives Heading-Level des Produkttitels (WCAG 2.1 AA - 1.3.1 / 2.4.6).
  // Wird je nach Kontext gesetzt, damit keine Heading-Ebenen übersprungen
  // werden (z. B. Kategorieliste unter <h1> => 2, Homepage-Slider unter
  // einem <h3>-Abschnitt => 4). Per aria-level am <h5> überschrieben, sodass
  // bestehende `h5.title`-Styles unverändert greifen.
  headingLevel = input<number>(5)

  picture = computed<PictureInterface | undefined>(() => {
    const p = this.product()
    const h = this.isHovered()
    const v = this.variantHovered()
    if (p) {
      if (v) {
        if (v.images.length) {
          return v.images[0]
        }
      }
      if (h) {
        try {
          if (p.getActionPicture()) {
            return p.getActionPicture()
          }
        } catch (e) {
          
        }
      }

      // apply visibility score
      const v2 = this.getHighestVisibilityScoreVariant(p)
      if (v2 && v2.images) {
        if (v2.images.length) {
          return v2.images[0]
        }
      }

      return p.getListPicture()
    }

    return undefined
  })

  badge = computed(() => {
    const p = this.product()
    const v = this.variantHovered()
    if (v) {
      return v.badge
    }
    return p.variants[0].badge
  })

  isHovered = signal(false)
  variantHovered = signal<ProductVariantInterface | undefined>(undefined)

  pageService = inject(PageService)
  storePickupEnabled = signal<boolean>(false)

  constructor(
    private filterService: FilterService,
  ) {
    this.pageService.getConfigSubject().subscribe(config => {
      if (config) this.storePickupEnabled.set(config.storePickupEnabled)
    })
  }

  getSizeList() {
    switch(this.scope) {
      case "list":
        // commented other media queries because of character limit
        return '' +
          '(max-width: 576px) 40vw, ' +
          //'(max-width: 768px) 230px, ' +
          //'(max-width: 992px) 200px, ' +
          //'(max-width: 1400px) 220px, ' +
          '260px'
      case "search":
        return ''+
        '(max-width: 767px) 30vw, ' +
        '200px'
    }
  }

  getSwatchVariants = computed<ProductVariantInterface[]>(() =>  {
    let swatchVariants : ProductVariantInterface[] = []

    if (! this.product()) {
      return swatchVariants
    }

    const preset = this.presetVariant()
    let sortedVariants: ProductVariantInterface[] = []
    if (preset) {
      if (this.product().variants) {
        sortedVariants = this.product().variants.sort((a,b) => {
          if (a.variantId == preset.variantId && b.variantId == preset.variantId) return 0
          if (a.variantId == preset.variantId && b.variantId != preset.variantId) return -1
          else return 1
        })
      }
    } else {
      sortedVariants = this.product().variants
    }

    for(let variant of sortedVariants) {
      if(!variant.swatchImage) {
        continue
      }
      if(swatchVariants.some(v => v.swatchImage == variant.swatchImage)) {
        continue
      }
      if(!this.product().canBeOrdered(this.storePickupEnabled(), 1, variant.variantId) && !this.product().isRemindPossible(variant)) {
        continue
      }

      swatchVariants.push(variant)
    }

    return swatchVariants
  })

  getSwatchAttribute(variant: ProductVariantInterface | undefined): ProductAttributeInterface | undefined {
    if (! (this.productAttributes instanceof Map) || variant == undefined || variant.attributes == undefined) {
      return undefined
    }

    // search for product swatch attribute in all productAttributes
    for (var attribute of this.productAttributes.values()) {
      let isSwatch: boolean = false
      for (var option of attribute.options) {
        if (option.imageSlug) {
          isSwatch = true
          break
        }
      }

      if (isSwatch) {
        const variantOptionId = variant.attributes.get(attribute.code)
        if (variantOptionId == undefined) {
          continue
        }

        for (var option of attribute.options) {
          if (option.optionIds.includes(variantOptionId)) {
            return attribute
          }
        }
      }
    }

    return undefined
  }

  getSwatchParams(variant: ProductVariantInterface | undefined, j: number) : string | undefined {

    if (! variant) {
      const p = this.product()
      if (p.variants && p.variants.length) {
        variant = p.variants[0]
      }
    }

    if (variant == undefined || variant.attributes == undefined) {
      return undefined
    }

    let params = ""

    // color
    let hasSwatch = false
    if (j < 4) {
      const attribute = this.getSwatchAttribute(variant)
      if (attribute) {
        params += attribute.code + "=" + variant.attributes.get(attribute.code)
        hasSwatch = true
      }
    }

    // filtered options (one per attribute_code)
    const fs = this.filterService.getFilterSelections()()
    if (fs) {
      for (const filter of Object.values(fs as FilterSelectionsInterface)) {
        if (filter.selections && filter.selections.length > 0) {

          // do not add color_group if swatch filter is set
          if (filter.attributeCode == 'color_group' && hasSwatch) continue

          if (params.length > 0) {
            params += "&"
          }
          params += filter.attributeCode + "=" + filter.selections[0]
        }
      }
    }
    return params.length > 0 ? params : undefined
  }

  private getHighestVisibilityScoreVariant(p: Product<ProductVariantInterface>): ProductVariantInterface | undefined {

    let variants = this.getSwatchVariants()
    if(variants.length <= 0) {
      // if there are no swatch variants
      variants = p.variants
    }

    let highest_vscore : ProductVariantInterface = variants[0]
    // check first 4, because everything after that is not visible
    for(var i = 1; (i < variants.length) && (i < 4); i++) {
      if(highest_vscore.visibilityScore < variants[i].visibilityScore) {
        highest_vscore = variants[i]
      }
    }

    return highest_vscore
  }

  shouldShowSwatch(j: number): boolean {
    const swatchesCount = this.getSwatchVariants().length
    const currentIndex = j + 1
    if (swatchesCount <= 5) return true
    if (currentIndex <= 4) return true
    return false
  }

  protected readonly mapNumberToLocaleString = mapNumberToLocaleString;
}
